import React, { useState, useEffect, useMemo } from 'react';
import { Sun, DoorOpen, Bell, MessageSquare, ArrowRight, CheckCircle2, Calendar, Mail, ClipboardCheck, Clock } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

// Mini Timeline Component
const MiniTimeline = ({ eventos }) => {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(interval);
    }, []);

    // Separar eventos de todo el día vs con hora
    const allDayEvents = eventos.filter(e => e.todo_el_dia);
    const timedEvents = eventos.filter(e => !e.todo_el_dia).map(e => ({
        ...e,
        start: new Date(e.hora_inicio),
        end: new Date(e.hora_fin)
    })).sort((a, b) => a.start - b.start);

    if (timedEvents.length === 0 && allDayEvents.length === 0) return null;

    // Calcular rango dinámico (solo horas con eventos, +30min padding)
    let rangeStart, rangeEnd;
    if (timedEvents.length > 0) {
        const firstStart = timedEvents[0].start;
        const lastEnd = timedEvents.reduce((max, e) => e.end > max ? e.end : max, timedEvents[0].end);

        rangeStart = new Date(firstStart);
        rangeStart.setMinutes(rangeStart.getMinutes() - 30);
        rangeStart.setMinutes(0); // Redondear a hora exacta

        rangeEnd = new Date(lastEnd);
        rangeEnd.setMinutes(rangeEnd.getMinutes() + 30);
        rangeEnd.setMinutes(0);
        rangeEnd.setHours(rangeEnd.getHours() + 1); // Redondear arriba
    } else {
        // Solo eventos de todo el día, no mostrar timeline de horas
        return (
            <div className="mb-4">
                {allDayEvents.map((evento, i) => (
                    <div
                        key={i}
                        onClick={evento.link ? () => window.open(evento.link, '_blank') : undefined}
                        className={`px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 mb-1.5 last:mb-0 ${evento.link ? 'cursor-pointer hover:bg-indigo-100' : ''} transition-colors`}
                    >
                        <div className="flex items-center gap-2">
                            <Calendar size={12} className="text-indigo-500 shrink-0" />
                            <span className="text-xs font-medium text-indigo-700 truncate">{evento.titulo}</span>
                        </div>
                        <span className="text-[10px] text-indigo-400 ml-5">Todo el día</span>
                    </div>
                ))}
            </div>
        );
    }

    const totalMinutes = (rangeEnd - rangeStart) / 60000;
    const PIXELS_PER_HOUR = 32;
    const totalHeight = (totalMinutes / 60) * PIXELS_PER_HOUR;

    const getTopPosition = (date) => {
        const minutes = (date - rangeStart) / 60000;
        return (minutes / totalMinutes) * totalHeight;
    };

    // Generar marcas de hora
    const hourMarks = [];
    const h = new Date(rangeStart);
    while (h <= rangeEnd) {
        hourMarks.push(new Date(h));
        h.setHours(h.getHours() + 1);
    }

    // Posición de la línea "ahora"
    const nowInRange = now >= rangeStart && now <= rangeEnd;
    const nowTop = nowInRange ? getTopPosition(now) : null;

    // Colores para distinguir eventos
    const eventColors = [
        { bg: 'bg-indigo-100', border: 'border-indigo-200', text: 'text-indigo-700', time: 'text-indigo-500' },
        { bg: 'bg-violet-100', border: 'border-violet-200', text: 'text-violet-700', time: 'text-violet-500' },
        { bg: 'bg-sky-100', border: 'border-sky-200', text: 'text-sky-700', time: 'text-sky-500' },
        { bg: 'bg-teal-100', border: 'border-teal-200', text: 'text-teal-700', time: 'text-teal-500' },
        { bg: 'bg-amber-100', border: 'border-amber-200', text: 'text-amber-700', time: 'text-amber-500' },
    ];

    const fmt = (d) => d.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: false });

    return (
        <div className="mb-4">
            {/* Eventos de todo el día */}
            {allDayEvents.length > 0 && (
                <div className="mb-3">
                    {allDayEvents.map((evento, i) => (
                        <div
                            key={i}
                            onClick={evento.link ? () => window.open(evento.link, '_blank') : undefined}
                            className={`px-3 py-2 rounded-lg bg-indigo-50 border border-indigo-100 mb-1.5 last:mb-0 ${evento.link ? 'cursor-pointer hover:bg-indigo-100' : ''} transition-colors`}
                        >
                            <div className="flex items-center gap-2">
                                <Calendar size={12} className="text-indigo-500 shrink-0" />
                                <span className="text-xs font-medium text-indigo-700 truncate">{evento.titulo}</span>
                            </div>
                            <span className="text-[10px] text-indigo-400 ml-5">Todo el día</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Timeline de eventos con hora */}
            <div className="relative" style={{ height: totalHeight }}>
                {/* Marcas de hora */}
                {hourMarks.map((hour, i) => {
                    const top = getTopPosition(hour);
                    const label = hour.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', hour12: false });
                    return (
                        <div key={i} className="absolute left-0 right-0 flex items-center" style={{ top }}>
                            <span className="text-[10px] text-gray-400 w-10 shrink-0 tabular-nums">{label}</span>
                            <div className="flex-1 border-t border-gray-100"></div>
                        </div>
                    );
                })}

                {/* Bloques de eventos */}
                {timedEvents.map((evento, i) => {
                    const top = getTopPosition(evento.start);
                    const height = Math.max(((evento.end - evento.start) / 60000 / totalMinutes) * totalHeight, 24);
                    const colors = eventColors[i % eventColors.length];

                    return (
                        <div
                            key={i}
                            className={`absolute left-11 right-0 ${colors.bg} ${colors.border} border rounded-lg px-2.5 py-1.5 overflow-hidden ${evento.link ? 'cursor-pointer hover:brightness-95' : ''} transition-all`}
                            style={{ top, height }}
                            onClick={evento.link ? () => window.open(evento.link, '_blank') : undefined}
                        >
                            <p className={`text-xs font-semibold ${colors.text} truncate leading-tight`}>{evento.titulo}</p>
                            {height >= 36 && (
                                <p className={`text-[10px] ${colors.time} truncate`}>
                                    {fmt(evento.start)} – {fmt(evento.end)}
                                    {evento.ubicacion ? ` · ${evento.ubicacion}` : ''}
                                </p>
                            )}
                        </div>
                    );
                })}

                {/* Línea "ahora" */}
                {nowInRange && (
                    <div className="absolute left-0 right-0 flex items-center z-10" style={{ top: nowTop }}>
                        <div className="w-2 h-2 rounded-full bg-red-500 shrink-0"></div>
                        <div className="flex-1 border-t-2 border-red-400"></div>
                    </div>
                )}
            </div>
        </div>
    );
};

// onListo: avisa cuando el panel ya tiene que mostrarse (el lazo rosa se
// cuelga de el desde Inicio).
const MiDiaWidget = ({ onListo }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [eventos, setEventos] = useState([]);
    const [correoData, setCorreoData] = useState({ sin_leer: 0, correos: [] });
    const [aprobaciones, setAprobaciones] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMiDia();
        fetchCalendario();
        fetchCorreo();
        fetchAprobaciones();
    }, []);

    const fetchMiDia = async () => {
        try {
            const response = await axiosInstance.get('/api/widgets/mi-dia');
            if (response.data.success) {
                setData(response.data);
            }
        } catch (error) {
            console.log('Mi Día API not available yet');
        } finally {
            setLoading(false);
        }
    };

    const fetchCalendario = async () => {
        try {
            const response = await axiosInstance.get('/api/widgets/mi-calendario');
            if (response.data.success && response.data.eventos) {
                setEventos(response.data.eventos);
            }
        } catch (error) {
            // Silencioso: si falla, simplemente no mostramos eventos
        }
    };

    const fetchCorreo = async () => {
        try {
            const response = await axiosInstance.get('/api/widgets/mi-correo');
            if (response.data.success) {
                setCorreoData(response.data);
            }
        } catch (error) {
            // Silencioso
        }
    };

    const fetchAprobaciones = async () => {
        try {
            const response = await axiosInstance.get('/api/widgets/mis-aprobaciones');
            if (response.data.success && response.data.aprobaciones) {
                setAprobaciones(response.data.aprobaciones);
            }
        } catch (error) {
            // Silencioso
        }
    };

    useEffect(() => {
        if (!loading && data && onListo) onListo();
    }, [loading, data, onListo]);

    if (loading) return null;
    if (!data) return null;

    const hasSalas = data.salas_hoy && data.salas_hoy.length > 0;
    const hasNotifs = data.notificaciones_sin_leer > 0;
    const hasChats = data.chats_sin_leer > 0;
    const hasEventos = eventos.length > 0;
    const hasCorreos = correoData.sin_leer > 0;
    const hasAprobaciones = aprobaciones.length > 0;
    const hasAnything = hasSalas || hasNotifs || hasChats || hasEventos || hasCorreos || hasAprobaciones;

    // Items de lista (ya NO incluye eventos de calendario)
    const items = [];

    if (hasSalas) {
        data.salas_hoy.forEach((sala) => {
            items.push({
                icon: <DoorOpen size={16} />,
                label: `${sala.sala} — ${sala.hora_inicio} a ${sala.hora_fin}`,
                sublabel: sala.motivo,
                color: 'text-emerald-600 bg-emerald-50',
                onClick: () => navigate('/reserva-salas')
            });
        });
    }

    if (hasNotifs) {
        items.push({
            icon: <Bell size={16} />,
            label: `${data.notificaciones_sin_leer} notificación${data.notificaciones_sin_leer > 1 ? 'es' : ''} sin leer`,
            color: 'text-amber-600 bg-amber-50',
            onClick: null
        });
    }

    // Correos sin leer de Gmail
    if (hasCorreos) {
        const correosToShow = correoData.correos.slice(0, 3);
        correosToShow.forEach((c) => {
            items.push({
                icon: <Mail size={16} />,
                label: c.asunto,
                sublabel: c.remitente,
                color: 'text-purple-600 bg-purple-50',
                onClick: () => window.open('https://mail.google.com', '_blank')
            });
        });
        if (correoData.sin_leer > correosToShow.length) {
            items.push({
                icon: <Mail size={16} />,
                label: `+${correoData.sin_leer - correosToShow.length} correo${correoData.sin_leer - correosToShow.length > 1 ? 's' : ''} más sin leer`,
                color: 'text-purple-400 bg-purple-50',
                onClick: () => window.open('https://mail.google.com', '_blank')
            });
        }
    }

    if (hasChats) {
        items.push({
            icon: <MessageSquare size={16} />,
            label: `${data.chats_sin_leer} chat${data.chats_sin_leer > 1 ? 's' : ''} sin leer`,
            color: 'text-blue-600 bg-blue-50',
            onClick: () => navigate('/chat')
        });
    }

    // Aprobaciones pendientes
    if (hasAprobaciones) {
        aprobaciones.forEach((aprob) => {
            items.push({
                icon: <ClipboardCheck size={16} />,
                label: aprob.titulo,
                sublabel: `${aprob.solicitante}${aprob.detalle ? ' · ' + aprob.detalle : ''}`,
                color: 'text-rose-600 bg-rose-50',
                onClick: aprob.ruta ? () => navigate(aprob.ruta) : null
            });
        });
    }

    return (
        <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="px-5 py-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white shadow-sm">
                    <Sun size={20} />
                </div>
                <div>
                    <h3 className="font-bold text-gray-900">Tu Día</h3>
                    <p className="text-xs text-gray-500">Resumen rápido de hoy</p>
                </div>
            </div>

            {/* Content */}
            <div className="px-5 pb-5">
                {/* Mini Timeline de calendario */}
                {hasEventos && <MiniTimeline eventos={eventos} />}

                {/* Lista de items */}
                {items.length > 0 ? (
                    <div className="space-y-2.5">
                        {items.map((item, index) => (
                            <div
                                key={index}
                                className={`flex items-center gap-3 p-3 rounded-xl border border-gray-50 ${item.onClick ? 'cursor-pointer hover:bg-gray-50 hover:border-gray-100' : ''} transition-all group`}
                                onClick={item.onClick}
                            >
                                <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center shrink-0`}>
                                    {item.icon}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">{item.label}</p>
                                    {item.sublabel && (
                                        <p className="text-xs text-gray-500 truncate">{item.sublabel}</p>
                                    )}
                                </div>
                                {item.onClick && (
                                    <ArrowRight size={14} className="text-gray-300 group-hover:text-gray-500 shrink-0 transition-colors" />
                                )}
                            </div>
                        ))}
                    </div>
                ) : !hasEventos ? (
                    <div className="text-center py-4">
                        <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3">
                            <CheckCircle2 size={24} className="text-emerald-500" />
                        </div>
                        <p className="text-sm font-medium text-gray-700">¡Todo al día!</p>
                        <p className="text-xs text-gray-400 mt-1">No tienes pendientes por ahora</p>
                    </div>
                ) : null}
            </div>
        </section>
    );
};

export default MiDiaWidget;
